/**
 * This service worker acts as a proxy for downloading encrypted files using the
 * dropbox.com web client. It is registered under the /encrypted_folder_download/ scope.
 *
 * When downloading an encrypted file, the dropbox.com web client wraps the download URL
 * in a proxy URL of the form: /encrypted_folder_download/proxy?downloadUrl=${downloadUrl}
 *
 * The service worker intercepts these requests, fetches the downloadUrl contents,
 * and decrypts them, returning a decrypted response.
 */
import {DecryptTransformStream} from 'metaserver/static/js/encrypted_folder/download_service_worker/transform_stream';
import type {DecryptTransformStreamMetadata} from 'metaserver/static/js/encrypted_folder/download_service_worker/types';
import {
  captureError,
  captureErrorEvent,
  capturePromiseRejection,
  init as initSentry,
  SentryError,
  setGlobalContext,
} from 'metaserver/static/js/encrypted_folder/download_service_worker/sentry_reporting';
import {computeDomains} from 'js/domains/compute_domains';

// Must re-declare self for proper service worker types
export declare const self: ServiceWorkerGlobalScope;

/**
 * This is used as allowlist of domains we allow dowloads from.  We may want
 * to expand this list over time.
 */
const domains = computeDomains(process.env.DEV_SERVER_HOSTNAME!)[0];
const WEB_BLOCK_DOMAINS = [domains.BLOCK_CLUSTER, domains.PUBSERVER];

/**
 * This is used to extend the allowlist of domains we allow downloads from. This corresponds
 * to the .dropboxusercontent.com suffix we use for pubserver domains
 */
const DUC_SUFFIX = '.' + domains.DROPBOX_USER_CONTENT_DOMAIN;

// Initialize Sentry for error reporting
initSentry(false);

self.addEventListener('error', (e) => {
  captureErrorEvent(e);
});

self.addEventListener('unhandledrejection', (e) => {
  capturePromiseRejection(e);
});

self.addEventListener('install', () => {
  self.skipWaiting();
});

self.addEventListener('activate', (event) => {
  event.waitUntil(self.clients.claim());
});

class MetadataCache {
  private items: Map<string, DecryptTransformStreamMetadata> = new Map();
  private onUpdateCallbacks: (() => void)[] = [];

  private registerOnUpdateCallback(callback: () => void) {
    this.onUpdateCallbacks.push(callback);
  }

  private removeOnUpdateCallback(callback: () => void) {
    this.onUpdateCallbacks = this.onUpdateCallbacks.filter((it) => it !== callback);
  }

  add(key: string, data: DecryptTransformStreamMetadata) {
    this.items.set(key, data);
    this.onUpdateCallbacks.forEach((it) => it());
  }

  metadataPromise(key: string) {
    return new Promise<DecryptTransformStreamMetadata>((resolve) => {
      const resolveToMetadataIfInCache = () => {
        if (this.items.has(key)) {
          const metadata = this.items.get(key)!;
          this.items.delete(key);
          this.removeOnUpdateCallback(resolveToMetadataIfInCache);
          resolve(metadata);
        }
      };

      this.registerOnUpdateCallback(resolveToMetadataIfInCache);

      resolveToMetadataIfInCache();
    });
  }
}

const metadataCache: MetadataCache = new MetadataCache();

self.addEventListener('message', (event) => {
  const [metadataKey, metadata]: [string, DecryptTransformStreamMetadata] = event.data;
  metadataCache.add(metadataKey, metadata);
});

const HTML_REDIRECT_REGEXP = /location\.replace\("(.+)"\)/;

/**
 * Fetches a URL and recursively follows redirects until reaching a non-redirect response.
 * Non-redirect response is defined as having a Content-Disposition header.
 *
 * This method is necessary because the Dropbox server will sometimes respond with an HTML
 * redirect instead of an HTTP redirect (e.g. for Safari clients). Normal HTTP redirects
 * are automatically followed by the browser during fetch, but HTML redirects need to be
 * parsed and followed with a subsequent fetch call.
 */
const fetchAndFollow = async (url: string): Promise<Response> => {
  const response = await fetch(url);
  const dispositionHeader = response.headers.get('Content-Disposition');
  if (dispositionHeader) {
    // Server responded with file contents.
    return response;
  }

  // Server responded with an HTML redirect. Parse the redirect URL from
  // the response body and follow it.
  const responseText = await response.text();
  const match = HTML_REDIRECT_REGEXP.exec(responseText);
  if (!match) {
    throw new SentryError('Could not find valid redirect URL in response body', {
      tags: {location: 'ServiceWorker.fetchAndFollow'},
      extras: {responseText: responseText},
    });
  }
  const redirectUrl = match[1];
  return fetchAndFollow(redirectUrl);
};

/**
 * Fetches a URL and decrypts the response body, returning a new response.
 * Error will currently be handled by the default download error handling with showing a toast message.
 */
const fetchAndDecrypt = async (url: string) => {
  const timeoutPromise = new Promise<DecryptTransformStreamMetadata>((_, reject) => {
    setTimeout(() => {
      reject(
        new SentryError('Timeout exceeded', {
          tags: {location: 'ServiceWorker.fetchAndDecrypt'},
        })
      );
    }, 10000);
  });

  const metadataKey = new URL(url).pathname;
  const metadata = await Promise.race([metadataCache.metadataPromise(metadataKey), timeoutPromise]);
  if (!metadata) {
    throw new SentryError('Did not receive relevant metadata on time', {
      tags: {location: 'ServiceWorker.fetchAndDecrypt'},
    });
  }

  const downloadDomain = new URL(url).hostname;

  const isBlockServerDomain =
    WEB_BLOCK_DOMAINS.includes(downloadDomain) ||
    downloadDomain.endsWith(DUC_SUFFIX) ||
    downloadDomain === domains.WEBSERVER;

  if (!isBlockServerDomain) {
    throw new SentryError('Invalid download URL', {
      tags: {location: 'ServiceWorker.fetchAndDecrypt'},
      extras: {downloadDomain: downloadDomain},
    });
  }

  const response = await fetchAndFollow(url);
  if (!response.body) {
    throw new SentryError('Response body is empty', {
      tags: {location: 'ServiceWorker.fetchAndDecrypt'},
      extras: {responseStatus: response.status, url: url},
    });
  }

  const decryptTransform =
    await DecryptTransformStream.validateAuthSetHmacAndCreateStream(metadata);
  const decryptedBody = response.body.pipeThrough(decryptTransform.readableWritablePair);

  return new Response(decryptedBody, {
    headers: response.headers,
    status: response.status,
    statusText: response.statusText,
  });
};

/**
 * Intercepts all fetch requests to /encrypted_folder_download/* paths.
 *
 * This event handler listens for requests of the form:
 * /encrypted_folder_download/proxy?downloadUrl=${downloadUrl}
 *
 * If it receives such a request, then it fetches the downloadUrl contents,
 * decrypts them, and returns a decrypted response back to the original request.
 *
 * Otherwise, it returns a network error.
 */
self.addEventListener('fetch', (event) => {
  const requestUrl = new URL(event.request.url);
  const downloadUrl = requestUrl.searchParams.get('downloadUrl');

  setGlobalContext(requestUrl.pathname, downloadUrl);

  if (requestUrl.pathname !== '/encrypted_folder_download/proxy' || !downloadUrl) {
    captureError(
      new SentryError('Unexpected path or params', {
        tags: {location: 'ServiceWorker.fetch'},
        severity: 'warning',
      })
    );
    // Unexpected path or params, return a network error
    return event.respondWith(Response.error());
  }
  event.respondWith(fetchAndDecrypt(downloadUrl));
});
